import { useMemo, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { create } from "zustand";
import { ctxFor, getData, type DataContext } from "@/data/store";
import { useSharedFolders } from "@/data/sharedFolders";
import { Button } from "@/ui/Button";
import { Input } from "@/ui/Input";
import { IconButton } from "@/ui/IconButton";
import { toast } from "@/ui/toast";
import s from "./HistoryDialog.module.css";
import f from "./FindReplaceDialog.module.css";

interface FindReplaceStore {
  open: boolean;
  setOpen: (open: boolean) => void;
}
export const useFindReplace = create<FindReplaceStore>((set) => ({
  open: false,
  setOpen: (open) => set({ open }),
}));

interface Match {
  id: string;
  title: string;
  count: number;
}

function countOccurrences(haystack: string, needle: string, caseSensitive: boolean): number {
  if (!needle) return 0;
  const h = caseSensitive ? haystack : haystack.toLowerCase();
  const n = caseSensitive ? needle : needle.toLowerCase();
  let count = 0;
  let idx = 0;
  while ((idx = h.indexOf(n, idx)) !== -1) {
    count++;
    idx += n.length;
  }
  return count;
}

function replaceAll(text: string, find: string, replace: string, caseSensitive: boolean): string {
  if (caseSensitive) return text.split(find).join(replace);
  // case-insensitive literal replace preserving surrounding text
  const lower = text.toLowerCase();
  const needle = find.toLowerCase();
  let out = "";
  let idx = 0;
  let last = 0;
  while ((idx = lower.indexOf(needle, last)) !== -1) {
    out += text.slice(last, idx) + replace;
    last = idx + find.length;
  }
  return out + text.slice(last);
}

export function FindReplaceDialog() {
  const { open, setOpen } = useFindReplace();
  const [find, setFind] = useState("");
  const [replace, setReplace] = useState("");
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [includeShared, setIncludeShared] = useState(false);
  const [matches, setMatches] = useState<Match[] | null>(null);
  const [busy, setBusy] = useState(false);
  // only folders the user OWNS may be included in bulk rewrites
  // (select the stable array, derive with useMemo: a .filter() inside the
  // selector returns a fresh reference every snapshot and loops React)
  const folders = useSharedFolders((st) => st.folders);
  const ownedFolders = useMemo(() => folders.filter((f) => f.role === "owner" && !f.locked), [folders]);

  /** Personal context, plus owned shared folder contexts when opted in. */
  const scopedContexts = (): DataContext[] => {
    const out = [getData()];
    if (includeShared) {
      for (const f of ownedFolders) {
        const ctx = ctxFor(f.id);
        if (ctx) out.push(ctx);
      }
    }
    return out;
  };

  const preview = async () => {
    if (!find || busy) return;
    setBusy(true);
    try {
      const found: Match[] = [];
      for (const data of scopedContexts()) {
        const notes = data.workspace.getSnapshot().filter((i) => i.type === "note" && !i.trashedAt);
        for (const item of notes) {
          const md = await data.notes.readMarkdown(item.id);
          const count = countOccurrences(md, find, caseSensitive);
          if (count > 0) found.push({ id: item.id, title: item.title || "Untitled", count });
        }
      }
      found.sort((a, b) => b.count - a.count);
      setMatches(found);
    } finally {
      setBusy(false);
    }
  };

  const applyAll = async () => {
    if (!matches || matches.length === 0 || busy) return;
    const total = matches.reduce((sum, m) => sum + m.count, 0);
    const sharedWarning = includeShared && ownedFolders.length ? " This includes shared folders you own, so every member sees the change." : "";
    if (!confirm(`Replace ${total} occurrence${total === 1 ? "" : "s"} of "${find}" across ${matches.length} note${matches.length === 1 ? "" : "s"}? Each note's change is one undoable edit.${sharedWarning}`)) {
      return;
    }
    setBusy(true);
    try {
      const contexts = scopedContexts();
      for (const m of matches) {
        const data = contexts.find((c) => c.workspace.getItem(m.id)) ?? contexts[0]!;
        const handle = data.notes.open(m.id);
        await handle.whenLoaded;
        const current = handle.text.toString();
        const next = replaceAll(current, find, replace, caseSensitive);
        if (next !== current) {
          handle.doc.transact(() => {
            handle.text.delete(0, handle.text.length);
            handle.text.insert(0, next);
          });
          data.workspace.touch(m.id);
        }
        handle.release();
      }
      toast.success(`Replaced ${total} occurrence${total === 1 ? "" : "s"} in ${matches.length} note${matches.length === 1 ? "" : "s"}`);
      setMatches(null);
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setMatches(null);
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className={s.overlay} />
        <Dialog.Content className={f.dialog}>
          <div className={s.header}>
            <Dialog.Title className={s.title}>Find and replace in all notes</Dialog.Title>
            <Dialog.Close asChild>
              <IconButton label="Close">
                <X size={14} />
              </IconButton>
            </Dialog.Close>
          </div>
          <div className={f.body}>
            <Input placeholder="Find (literal text, includes code blocks)" value={find} autoFocus onChange={(e) => { setFind(e.target.value); setMatches(null); }} />
            <Input placeholder="Replace with" value={replace} onChange={(e) => setReplace(e.target.value)} />
            <label className={f.check}>
              <input type="checkbox" checked={caseSensitive} onChange={(e) => { setCaseSensitive(e.target.checked); setMatches(null); }} />
              Match case
            </label>
            {ownedFolders.length > 0 ? (
              <label className={f.check}>
                <input type="checkbox" checked={includeShared} onChange={(e) => { setIncludeShared(e.target.checked); setMatches(null); }} />
                Include shared folders I own ({ownedFolders.map((fo) => fo.title).join(", ")})
              </label>
            ) : null}
            {matches !== null ? (
              matches.length === 0 ? (
                <div className={f.empty}>No matches found.</div>
              ) : (
                <div className={f.matches}>
                  {matches.map((m) => (
                    <div key={m.id} className={f.match}>
                      <span className={f.matchTitle}>{m.title}</span>
                      <span className={f.matchCount}>{m.count}</span>
                    </div>
                  ))}
                </div>
              )
            ) : null}
          </div>
          <div className={s.actions}>
            <Button variant="ghost" disabled={!find || busy} onClick={() => void preview()}>
              {busy && matches === null ? "Scanning…" : "Preview matches"}
            </Button>
            <Button variant="solid" disabled={!matches || matches.length === 0 || busy} onClick={() => void applyAll()}>
              Replace all
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
